import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export type ButtonVariantProps = VariantProps<typeof buttonVariants.base>

export type ButtonIntent = NonNullable<ButtonVariantProps['intent']>

export type ButtonColor = NonNullable<ButtonVariantProps['color']>

export type ButtonSize = NonNullable<ButtonVariantProps['size']>

export type ButtonShape = NonNullable<ButtonVariantProps['shape']>

export type ButtonEmphasis = NonNullable<ButtonVariantProps['emphasis']>

export interface FavoriteButtonProps {
  type?: 'article' | 'book' | 'journal' | 'author' | 'template'
  selected?: boolean
  intent?: 'filled' | 'outlined'
  layout?: 'list' | 'reading'
}

export const buttonVariants = {
  base: cva([
    'size-fit',
    'no-underline',
    'select-none',
    'font-body',
    'flex',
    'shrink-0',
    'justify-center',
    'items-center',
    'ease-out',
    'duration-300',
    'transition-colors',
  ], {
    variants: {
      intent: {
        primary: [],
        secondary: ['outline', 'outline-1', 'outline-[currentColor]'],
        tertiary: [],
        text: ['bg-transparent', 'outline-none', 'p-0'],
      },
      // cva concatenates classes without resolving Tailwind conflicts, so
      // properties that differ per size (font weight, square radius) live on
      // the size variants/compounds instead of the base list.
      shape: {
        square: [],
        rounded: ['rounded-full'],
      },
      size: {
        // Compact action button (Figma: 32px height, caption 12/18 regular).
        xsmall: ['h-8', 'px-s', 'gap-3xs', 'text-xs', 'font-normal'],
        small: ['py-2.5xs', 'px-m', 'gap-xs', 'text-sm', 'font-medium'],
        medium: ['py-s', 'px-l', 'gap-s', 'text-base', 'font-medium'],
        large: ['py-m', 'px-l', 'gap-s', 'text-xl', 'font-medium'],
      },
      color: {
        blurple: ['text-blurple-500'],
        moss: ['text-moss-500'],
        carbon: [],
        // Literal black text for pinned-light surfaces (reading-mode
        // tooltips stay white in dark mode, so themed tokens lose contrast).
        neutral: ['text-neutral-black'],
      },
      emphasis: {
        standard: [],
        inverted: [],
      },
      disabled: {
        true: ['text-carbon-400', 'cursor-not-allowed', 'focus-visible:outline-offset-0', 'focus-visible:outline-none'],
        false: ['focus-visible:ring-3', 'focus-visible:ring-offset-4', 'focus-visible:ring-blurple-300', 'cursor-pointer'],
      },
    },
    compoundVariants: [
      {
        // Neutral tertiary buttons (pinned-light surfaces) stay background-less
        // in every state, including disabled.
        disabled: true,
        color: ['blurple', 'moss', 'carbon'],
        class: ['bg-carbon-100'],
      },
      {
        disabled: true,
        color: 'neutral',
        intent: ['primary', 'secondary', 'text'],
        class: ['bg-carbon-100'],
      },
      {
        intent: 'secondary',
        disabled: false,
        class: 'hover:outline-2',
      },
      {
        intent: 'primary',
        color: 'blurple',
        disabled: false,
        emphasis: 'standard',
        class: [
          'bg-blurple-100',
          'text-blurple-500',
          'hover:bg-blurple-50',
          'active:bg-blurple-200',
          'dark:bg-blurple-300',
          // FIXME: Would make more sense if this was blurple-300, but the sketches say 100
          'dark:focus-visible:bg-blurple-100',
        ],
      },
      {
        intent: 'primary',
        color: 'blurple',
        emphasis: 'inverted',
        disabled: false,
        class: [
          'bg-blurple-500',
          'text-neutral-white',
          'hover:bg-blurple-400',
          'active:bg-blurple-300',
        ],
      },
      {
        intent: ['secondary', 'tertiary'],
        color: 'blurple',
        disabled: false,
        class: [
          'hover:bg-blurple-50',
          'active:bg-blurple-200',
          'dark:text-blurple-300',
          'dark:active:text-blurple-500',
          'dark:active:outline-blurple-300',
        ],
      },
      {
        intent: 'primary',
        color: 'moss',
        disabled: false,
        class: [
          'dark:text-moss-500',
          'dark:active:text-moss-500',
          'bg-moss-200',
          'hover:bg-moss-100',
          'active:bg-moss-300',
          'active:text-moss-200',
        ],
      },
      {
        intent: ['secondary', 'tertiary'],
        color: 'moss',
        disabled: false,
        class: [
          'hover:bg-moss-100',
          'active:bg-moss-300',
          'active:text-moss-200',
          'active:outline-moss-500',
          'dark:bg-transparent',
          'dark:text-moss-200',
          'dark:hover:bg-moss-100',
          'dark:hover:text-moss-500',
          'dark:hover:outline-moss-200',
          'dark:active:bg-moss-300',
          'dark:active:text-moss-200',
        ],
      },
      {
        intent: 'primary',
        color: 'carbon',
        disabled: false,
        class: [
          'text-carbon-50',
          'bg-carbon-500',
          'hover:bg-carbon-300',
          'active:bg-carbon-400',
          'dark:bg-carbon-500',
          'dark:text-carbon-50',
          'dark:hover:bg-carbon-400',
          'dark:active:bg-carbon-300',
          'dark:active:text-carbon-50',
        ],
      },
      {
        intent: ['secondary', 'tertiary'],
        color: 'carbon',
        disabled: false,
        class: [
          'text-carbon-500',
          'hover:text-carbon-50',
          'hover:bg-carbon-300',
          'active:text-carbon-50',
          'active:bg-carbon-400',
          'dark:text-carbon-50',
          'dark:hover:text-carbon-500',
          'dark:hover:bg-carbon-200',
          'dark:active:bg-carbon-300',
        ],
      },
      {
        intent: 'secondary',
        color: 'carbon',
        disabled: false,
        class: [
          'hover:outline-carbon-500',
          'dark:hover:outline-carbon-50',
        ],
      },
      {
        shape: 'square',
        size: 'small',
        class: [
          'pl-xs',
          'pr-s',
        ],
      },
      {
        shape: 'square',
        size: ['small', 'medium', 'large'],
        class: ['rounded'],
      },
      {
        shape: 'square',
        size: 'xsmall',
        class: ['rounded-lg'],
      },
      {
        intent: 'primary',
        color: 'neutral',
        disabled: false,
        class: [
          'bg-blurple-100',
          'hover:bg-blurple-50',
          'active:bg-blurple-200',
          // Pinned-light surface: keep the light background in dark mode.
          'dark:bg-blurple-100',
        ],
      },
      {
        intent: 'tertiary',
        color: 'neutral',
        disabled: false,
        class: [
          'bg-transparent',
          'hover:bg-carbon-100',
        ],
      },
    ],
  }),

  favorite: {
    base: cva([
      'cursor-pointer',
      'hover:bg-blurple-200',
      'disabled:hover:bg-carbon-100',
      'disabled:cursor-not-allowed',
      'flex',
      'items-center',
      'rounded',
      'shrink-0',
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-border-primary',
    ], {
      variants: {
        intent: {
          filled: ['bg-carbon-100'],
          outlined: [],
        },
        type: {
          article: [
            'group-hover:bg-blurple-200',
          ],
          book: [],
          journal: [],
          author: [],
          template: [],

        },
        selected: {
          true: [],
          false: [],
        },
        layout: {
          list: [
            'h-8',
            'w-8',
            'rounded',
            'justify-center',
          ],
          reading: [
            'w-fit',
            'p-xs',
            'nav:px-s',
            'nav:py-2.5xs',
            'gap-xs',
            'text-xsmall',
            'font-medium',
          ],
        },
      },
      compoundVariants: [
        {
          type: ['book', 'article', 'journal'],
          intent: 'filled',
          layout: 'list',
          class: [
            'absolute',
            'top-8',
            'right-8',
            'ease-in-out',
            'transition-all',
            'duration-300',
          ],
        },
        {
          layout: 'reading',
          intent: 'filled',
          class: [
            'bg-carbon-100',
            'dark:bg-surface-elevated',
            'dark:hover:bg-surface-secondary',
            'dark:text-carbon-50',
          ],
        },
        {
          layout: 'reading',
          intent: 'filled',
          type: 'book',
          class: [
            'absolute',
            'top-8',
            'right-8',
            'bg-carbon-100',
            'dark:bg-surface-elevated',
            'dark:hover:bg-surface-secondary',
            'dark:text-carbon-50',
          ],
        },
      ],
    }),
  },

  control: {
    base: cva([
      'flex',
      'items-center',
      'justify-center',
      'rounded-lg',
      'w-fit',
      'no-underline',
    ], {
      variants: {
        selected: {
          true: [],
          false: ['hover:border-transparent'],
        },
        size: {
          xsmall: ['text-xs'],
          small: ['text-xs'],
          medium: ['text-sm'],
          large: ['text-base'],
        },
        type: {
          filled: [],
          outlined: ['outline-1', 'outline-neutral-black'],
          // Ghost: no resting surface, background on hover/focus only. The
          // hover belongs to the type rather than a `selected: false`
          // compound — callers that leave `selected` unset would lose it.
          ghost: ['outline-none', 'outline-transparent', 'bg-transparent'],
        },
        iconOnly: {
          true: [],
          false: ['px-s', 'py-2xs'],
        },
        // Pinned-light surface (reading-mode tooltips stay white in dark
        // mode): keeps literal colors instead of themed/dark tokens.
        pinned: {
          true: [],
          false: [],
        },
      },
      compoundVariants: [
        {
          iconOnly: true,
          size: ['small', 'medium', 'large'],
          class: ['p-2xs'],
        },
        {
          iconOnly: true,
          size: 'xsmall',
          class: ['p-3xs'],
        },
        {
          selected: false,
          pinned: false,
          type: ['filled', 'outlined'],
          class: ['hover:bg-surface-secondary', 'group-hover:bg-surface-secondary'],
        },
        {
          selected: false,
          pinned: true,
          type: ['filled', 'outlined'],
          class: ['hover:bg-carbon-100'],
        },
        // Ghost carries no resting surface, so hover and focus are the only
        // feedback it has. Both keep the literal blurple hover the reading
        // tooltips had before these variants existed; they are split by
        // `pinned` only because a background shift alone is too faint to serve
        // as a focus indicator on the tooltips' light surface.
        {
          pinned: false,
          type: 'ghost',
          class: ['hover:bg-blurple-100', 'focus-visible:bg-blurple-200'],
        },
        {
          pinned: true,
          type: 'ghost',
          class: ['focus-visible:outline-none', 'focus-visible:ring-2', 'focus-visible:ring-blurple-300'],
        },
        // A labelled tooltip button reads as a button from its text and takes
        // the blurple hover. An icon-only one carries a grey chip surface of
        // its own, and keeps the hover inside that surface.
        {
          pinned: true,
          type: 'ghost',
          iconOnly: false,
          class: ['hover:bg-blurple-100'],
        },
        {
          pinned: true,
          type: 'ghost',
          iconOnly: true,
          class: ['hover:bg-carbon-100'],
        },
        {
          // Pinned surfaces set explicit child colors; text-primary is themed
          // and would flip on the always-white tooltips.
          pinned: false,
          type: 'ghost',
          class: ['text-primary'],
        },
        // A label needs air around the icon; the gap grows with the button.
        {
          iconOnly: false,
          size: 'xsmall',
          class: ['gap-3xs'],
        },
        {
          iconOnly: false,
          size: ['small', 'medium'],
          class: ['gap-2xs'],
        },
        {
          iconOnly: false,
          size: 'large',
          class: ['gap-xs'],
        },
        {
          type: 'outlined',
          pinned: false,
          class: ['dark:outline-carbon-50'],
        },
        {
          type: 'outlined',
          pinned: true,
          class: ['text-blurple-500'],
        },
        {
          selected: true,
          type: ['outlined'],
          class: ['bg-neutral-black', 'text-inverse', 'dark:bg-carbon-200', 'dark:text-carbon-500'],
        },
        {
          selected: false,
          type: 'filled',
          class: ['bg-surface-primary', 'dark:bg-surface-primary', 'text-primary'],
        },
        {
          selected: true,
          type: 'filled',
          class: ['dark:bg-white'],
        },
      ],
    }),
  },
}
